import {
  isBigDesktop,
  isDesktop,
  isMobile,
  isTablet,
} from "@customer-tools-web/component-ui";
import { debounce } from "lodash";
import * as React from "react";

import { TypeOmit } from "../const";

const ScreenSizeContext = React.createContext({
  isLessThanMobile: isMobile(window.innerWidth),
  isLessThanTablet: isTablet(window.innerWidth),
  screenWidth: window.innerWidth,
  isDesktop: isDesktop(window.innerWidth),
  isBigDesktop: isBigDesktop(window.innerWidth),
});

type ScreenSizeContextProviderProps = {
  screenWidth: number;
};

const ScreenSizeContextProvider: React.FC<ScreenSizeContextProviderProps> = (
  props
) => {
  const { screenWidth } = props;
  const value = React.useMemo(() => {
    return {
      isLessThanMobile: isMobile(screenWidth),
      isLessThanTablet: isTablet(screenWidth),
      isDesktop: isDesktop(screenWidth),
      isBigDesktop: isBigDesktop(screenWidth),
      screenWidth,
    };
  }, [screenWidth]);

  return (
    <ScreenSizeContext.Provider value={value}>
      {props.children}
    </ScreenSizeContext.Provider>
  );
};

class ScreenSizeProvider extends React.Component {
  state = {
    screenWidth: window.innerWidth,
  };
  private onWindowResize = debounce(() => {
    this.setState({ screenWidth: window.innerWidth });
  }, 300);
  public componentDidMount() {
    window.addEventListener("resize", this.onWindowResize);
  }
  public componentWillUnmount() {
    window.removeEventListener("resize", this.onWindowResize);
  }
  render() {
    const { screenWidth } = this.state;
    return (
      <ScreenSizeContextProvider screenWidth={screenWidth}>
        {this.props.children}
      </ScreenSizeContextProvider>
    );
  }
}

type screenSizeProps = {
  isLessThanTablet: boolean;
  isLessThanMobile: boolean;
  screenWidth: number;
  isDesktop: boolean;
  isBigDesktop: boolean;
};

const withScreenSize =
  <Props extends screenSizeProps>(
    ChildComponent: React.ComponentType<Props>
  ): React.ComponentType<TypeOmit<Props, keyof screenSizeProps>> =>
  (props) =>
    (
      <ScreenSizeContext.Consumer>
        {(context) => <ChildComponent {...context} {...(props as Props)} />}
      </ScreenSizeContext.Consumer>
    );

function useScreenSize() {
  return React.useContext(ScreenSizeContext);
}

export { ScreenSizeProvider, withScreenSize, screenSizeProps, useScreenSize };
